import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import axios from '../utils/axiosInstance';
import logo from '../assets/calum_logo_white.png';

/**
 * Login page: enter an email address, receive a 6-digit code, enter the code.
 */
const Login = () => {
  const { isAuthenticated, login } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Connexion | Costy';
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post('api/auth/request/', { email: email.trim() });
      setCode('');
      setCodeSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Envoi du code impossible. Vérifiez la connexion et réessayez.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post('api/auth/verify/', { email: email.trim(), code: code.trim() });
      await login(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Connexion impossible. Vérifiez la connexion et réessayez.');
      setBusy(false);
    }
  };

  const fieldClass = 'w-full min-h-[52px] rounded-xl border-0 px-4 text-center text-base';

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6 py-10" style={{ background: 'hsl(var(--mustard))' }}>
      <img src={logo} alt="Calum" className="w-full max-w-[240px] sm:max-w-[300px] h-auto" />
      <h1 className="mt-2 text-center text-3xl" style={{ color: 'hsl(var(--green))' }}>Costy</h1>
      <p className="mt-1 text-center font-medium" style={{ color: 'hsl(var(--green-deep))' }}>Le vrai coût de chaque assiette.</p>

      <div className="mt-8 w-full max-w-xs space-y-3">
        {error && <p role="alert" className="rounded-xl bg-card px-3 py-2 text-sm text-bad">{error}</p>}

        {!codeSent ? (
          <form onSubmit={requestCode} className="space-y-3">
            <label htmlFor="login-email" className="block text-center text-sm font-semibold" style={{ color: 'hsl(var(--green-deep))' }}>Votre adresse email</label>
            <input
              id="login-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom@calum.be"
              className={fieldClass}
            />
            <button type="submit" disabled={busy} className="btn-primary w-full !min-h-[52px]">
              {busy ? 'Envoi…' : 'Recevoir un code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-3">
            <p className="text-center text-sm" style={{ color: 'hsl(var(--green-deep))' }}>
              Si <b>{email.trim()}</b> a accès à Costy, un code vient d’y être envoyé. Il reste valable 15 minutes.
            </p>
            <label htmlFor="login-code" className="block text-center text-sm font-semibold" style={{ color: 'hsl(var(--green-deep))' }}>Code à 6 chiffres</label>
            <input
              id="login-code"
              type="text"
              required
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              className={`${fieldClass} tracking-[.3em] font-semibold`}
            />
            <button type="submit" disabled={busy} className="btn-primary w-full !min-h-[52px]">
              {busy ? 'Connexion…' : 'Se connecter'}
            </button>
            <div className="flex justify-between text-sm font-semibold" style={{ color: 'hsl(var(--green-deep))' }}>
              <button type="button" disabled={busy} onClick={requestCode} className="underline underline-offset-2 min-h-[40px]">Renvoyer un code</button>
              <button type="button" disabled={busy} onClick={() => { setCodeSent(false); setError(''); }} className="underline underline-offset-2 min-h-[40px]">Changer d’adresse</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
